import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { CollectionUpdateEvent } from '@shopify/events';
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

const finderSelector = 'mikan-equipment-filters, mikan-pump-filters';
let activeRequest;
let historyManaged = false;

function sameCollection(finder, url) {
  const base = new URL(finder.dataset.baseUrl, location.origin);
  const path = base.pathname.replace(/\/$/, '');
  return url.origin === base.origin && (url.pathname === path || url.pathname.startsWith(path + '/'));
}

function openResults(url) {
  url.hash = 'ResultsList';
  location.assign(url.href);
}

function scrollToResults(section, smooth) {
  const results = section.querySelector('#ResultsList');
  if (!results) return;
  results.setAttribute('tabindex', '-1');
  results.focus({ preventScroll: true });
  const header = document.getElementById('header-component');
  const headerSection = header?.closest('.header-section');
  const headerHeight = headerSection && ['sticky', 'fixed'].includes(getComputedStyle(headerSection).position) ? headerSection.offsetHeight : 0;
  const offset = Math.max(88, headerHeight + 16);
  const container = getScrollContainer();
  const containerTop = container.matches('.page-wrapper') ? container.getBoundingClientRect().top : 0;
  scrollTo({
    top: Math.max(0, results.getBoundingClientRect().top - containerTop + getScrollTop() - offset),
    behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'
  });
}

async function navigate(finder, url, fromHistory = false) {
  const section = finder.closest('[id^="shopify-section-"]');
  const sectionId = section?.id.replace('shopify-section-', '');
  if (!sectionId || !sameCollection(finder, url)) {
    openResults(url);
    return;
  }
  activeRequest?.abort();
  const controller = new AbortController();
  activeRequest = controller;
  const button = finder.form.querySelector('button[type="submit"]');
  finder.setAttribute('aria-busy', 'true');
  button.disabled = true;
  let timeout = setTimeout(() => controller.abort(), 15000);
  let deferred;
  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(url), controller.signal);
    if (controller.signal.aborted || activeRequest !== controller || !finder.isConnected) return;
    clearTimeout(timeout);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = parsed.getElementById(section.id);
    const nextFinder = nextSection?.querySelector(finder.localName);
    if (!nextFinder || !nextSection.querySelector('#ResultsList')) throw new Error('Missing collection results');
    const anchorTop = finder.getBoundingClientRect().top;
    finder.setAttribute('data-skip-node-update', '');
    nextFinder.setAttribute('data-skip-node-update', '');
    if (!fromHistory && url.href !== location.href) {
      history.replaceState({ ...history.state, mikanFilterNavigation: true }, '', location.href);
      history.pushState({ mikanFilterNavigation: true }, '', url.href);
    }
    historyManaged = true;
    const facets = nextSection.querySelector('facets-form-component');
    deferred = CollectionUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    finder.dispatchEvent(new CollectionUpdateEvent({
      collection: {
        id: facets?.dataset.collectionId || null,
        handle: facets?.dataset.collectionHandle || new URL(finder.dataset.baseUrl, location.origin).pathname.split('/').pop(),
        productsCount: Number(facets?.dataset.resultsCount) || 0
      },
      promise: deferred.promise
    }));
    await morphSection(sectionId, nextSection.outerHTML);
    if (activeRequest !== controller) return;
    if (nextFinder.dataset.resultCount !== undefined) finder.dataset.resultCount = nextFinder.dataset.resultCount;
    finder.syncFromUrl();
    if (nextFinder.dataset.headerCount) {
      const headerCount = document.querySelector('.mikan-collection-intro__count');
      if (headerCount) headerCount.textContent = nextFinder.dataset.headerCount;
    }
    scrollTo({ top: getScrollTop() + finder.getBoundingClientRect().top - anchorTop, behavior: 'instant' });
    deferred.resolve({ productsCount: Number(facets?.dataset.resultsCount) || 0 });
    requestAnimationFrame(() => {
      if (activeRequest === controller && section.isConnected) scrollToResults(section, !fromHistory);
    });
  } catch (error) {
    deferred?.reject(error);
    if (activeRequest === controller) openResults(url);
  } finally {
    clearTimeout(timeout);
    if (activeRequest === controller) {
      finder.removeAttribute('data-skip-node-update');
      finder.removeAttribute('aria-busy');
      button.disabled = false;
      finder.updateOptions();
    }
  }
}

document.addEventListener('mikan:filter-navigate', event => {
  const finder = event.target.closest(finderSelector);
  if (!finder || !event.detail?.url) return;
  event.preventDefault();
  navigate(finder, new URL(event.detail.url, location.origin));
});

document.addEventListener('click', event => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest('[data-equipment-clear], [data-pump-clear], [data-equipment-active] a, [data-pump-active] a, [data-equipment-browse]');
  const finder = link?.closest(finderSelector);
  if (!finder || link.target === '_blank' || link.hasAttribute('download')) return;
  event.preventDefault();
  navigate(finder, new URL(link.href));
});

window.addEventListener('popstate', event => {
  if (!historyManaged && !event.state?.mikanFilterNavigation) return;
  const finder = document.querySelector(finderSelector);
  if (finder && sameCollection(finder, new URL(location.href))) navigate(finder, new URL(location.href), true);
});

